import React, { useState, useEffect, useCallback, useMemo } from 'react';

const MathPowerGame = () => {
    const [activeMode, setActiveMode] = useState('squares');
    const [score, setScore] = useState(0);
    const [currentNumber, setCurrentNumber] = useState(0);
    const [currentMultiplier, setCurrentMultiplier] = useState(0);
    const [currentTriplet, setCurrentTriplet] = useState([0, 0, 0]);
    const [missingPosition, setMissingPosition] = useState(0);
    const [userAnswer, setUserAnswer] = useState('');
    const [feedback, setFeedback] = useState('');
    const [isCorrect, setIsCorrect] = useState(null);
    const [numberPool, setNumberPool] = useState([]); // eslint-disable-line no-unused-vars

    // Pythagorean triplets data
    const triplets = useMemo(() => [
        [3, 4, 5], [5, 12, 13], [8, 15, 17],
        [7, 24, 25], [20, 21, 29], [12, 35, 37],
        [9, 40, 41], [28, 45, 53], [11, 60, 61],
        [16, 63, 65], [33, 56, 65], [48, 55, 73],
        [13, 84, 85], [36, 77, 85], [39, 80, 89],
        [65, 72, 97]
    ], []);

    // Define all modes with their display names
    const modes = [
        { id: 'squares', name: 'Squares (1²-30²)' },
        { id: 'cubes', name: 'Cubes (1³-20³)' },
        { id: 'triplets', name: 'Triplets' },
        ...Array.from({ length: 8 }, (_, i) => ({
            id: (12 + i).toString(),
            name: `Table ${12 + i}`
        }))
    ];

    const generateQuestion = useCallback(() => {
        if (activeMode === 'triplets') {
            // For triplets mode
            const randomIndex = Math.floor(Math.random() * triplets.length);
            const randomTriplet = triplets[randomIndex];
            const randomPosition = Math.floor(Math.random() * 3); // 0, 1, or 2

            setCurrentTriplet(randomTriplet);
            setMissingPosition(randomPosition);
        }
        else if (activeMode === 'squares' || activeMode === 'cubes') {
            setNumberPool(prevPool => {
                let newPool = [...prevPool];
                if (newPool.length === 0) {
                    const max = activeMode === 'squares' ? 30 : 20;
                    newPool = Array.from({ length: max }, (_, i) => i + 1);
                    // Shuffle
                    for (let i = newPool.length - 1; i > 0; i--) {
                        const j = Math.floor(Math.random() * (i + 1));
                        [newPool[i], newPool[j]] = [newPool[j], newPool[i]];
                    }
                }
                setCurrentNumber(newPool[0]);
                return newPool.slice(1);
            });
        } else {
            // For multiplication tables
            setNumberPool(prevPool => {
                let newPool = [...prevPool];
                if (newPool.length === 0) {
                    newPool = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
                    // Shuffle
                    for (let i = newPool.length - 1; i > 0; i--) {
                        const j = Math.floor(Math.random() * (i + 1));
                        [newPool[i], newPool[j]] = [newPool[j], newPool[i]];
                    }
                }
                setCurrentMultiplier(newPool[0]);
                return newPool.slice(1);
            });
        }
        setUserAnswer('');
        setFeedback('');
        setIsCorrect(null);
    }, [activeMode, triplets]);

    useEffect(() => {
        setNumberPool([]);
        generateQuestion();
    }, [activeMode, generateQuestion]);

    const checkAnswer = () => {
        let correctAnswer;
        let questionText;

        if (activeMode === 'triplets') {
            correctAnswer = currentTriplet[missingPosition];
            questionText = `Triplet: ${currentTriplet.map((num, i) => i === missingPosition ? '?' : num).join(', ')}`;
        }
        else if (activeMode === 'squares') {
            correctAnswer = currentNumber * currentNumber;
            questionText = `${currentNumber}²`;
        }
        else if (activeMode === 'cubes') {
            correctAnswer = currentNumber * currentNumber * currentNumber;
            questionText = `${currentNumber}³`;
        }
        else {
            correctAnswer = parseInt(activeMode) * currentMultiplier;
            questionText = `${activeMode} × ${currentMultiplier}`;
        }

        if (parseInt(userAnswer) === correctAnswer) {
            setScore(score + 1);
            setFeedback('✅ Correct!');
            setIsCorrect(true);
        } else {
            setFeedback(`❌ Wrong! ${questionText} = ${correctAnswer}`);
            setIsCorrect(false);
        }
        setTimeout(generateQuestion, 1500);
    };

    const styles = {
        container: {
            textAlign: 'center',
            maxWidth: '800px',
            margin: '0 auto',
            minHeight: 'calc(100vh - 340px)',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
        },
        heading: {
            fontSize: 'clamp(1.5rem, 2vw, 1.5rem)',
            color: '#2c3e50',
            marginBottom: '30px',
            textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
        },
        filterContainer: {
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: '10px',
            marginBottom: '30px'
        },
        filterButton: {
            padding: '10px 16px',
            borderRadius: '20px',
            border: '1px solid #ddd',
            background: '#f5f5f5',
            cursor: 'pointer',
            fontSize: '14px',
            transition: 'all 0.3s ease',
            minWidth: '100px'
        },
        activeFilter: {
            background: '#4CAF50',
            color: 'white',
            borderColor: '#4CAF50',
        },
        questionDisplay: {
            fontSize: 'clamp(1.5rem, 2vw, 1.5rem)',
            margin: '20px 0',
            color: isCorrect === true ? '#27ae60' : isCorrect === false ? '#e74c3c' : '#2c3e50',
            fontWeight: '700',
            transition: 'color 0.3s ease'
        },
        tripletDisplay: {
            display: 'flex',
            justifyContent: 'center',
            gap: '10px',
            fontSize: '1.8rem',
            margin: '20px 0'
        },
        inputContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '15px',
            marginBottom: '20px'
        },
        input: {
            padding: '15px',
            fontSize: '1.2rem',
            width: '150px',
            textAlign: 'center',
            borderRadius: '8px',
            border: '2px solid #dfe6e9',
            outline: 'none',
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            maxWidth: '80%'
        },
        submitButton: {
            backgroundColor: '#007BFF',
            color: 'white',
            padding: '12px 30px',
            border: 'none',
            borderRadius: '50px',
            cursor: 'pointer',
            fontSize: '1.1rem',
            fontWeight: '600',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 8px rgba(0,123,255,0.3)',
            width: 'fit-content'
        },
        feedback: {
            fontSize: '1.3rem',
            margin: '15px 0',
            minHeight: '30px',
            fontWeight: '500',
            color: isCorrect === true ? '#27ae60' : isCorrect === false ? '#e74c3c' : 'transparent',
            transition: 'all 0.3s ease'
        },
        score: {
            fontSize: '1.2rem',
            color: '#2c3e50',
            fontWeight: '600',
            padding: '15px',
            backgroundColor: 'rgba(255,255,255,0.7)',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }
    };

    return (
        <div style={styles.container}>
            <h1 style={styles.heading}>Math Power Challenge ⚡</h1>

            {/* Chips-based navigation */}
            <div style={styles.filterContainer}>
                {modes.map((mode) => (
                    <button
                        key={mode.id}
                        onClick={() => setActiveMode(mode.id)}
                        style={{
                            ...styles.filterButton,
                            ...(activeMode === mode.id ? styles.activeFilter : {})
                        }}
                    >
                        {mode.name}
                    </button>
                ))}
            </div>

            {activeMode === 'triplets' ? (
                <div>
                    <div style={{ fontSize: '1.2rem', marginBottom: '10px' }}>Pythagorean Triplet:</div>
                    <div style={styles.tripletDisplay}>
                        {currentTriplet.map((num, index) => (
                            <div key={index}>
                                {index === missingPosition ? '?' : num}
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div style={styles.questionDisplay}>
                    {activeMode === 'squares' ? `What is ${currentNumber}²?` :
                        activeMode === 'cubes' ? `What is ${currentNumber}³?` :
                            `What is ${activeMode} × ${currentMultiplier}?`}
                </div>
            )}

            <div style={styles.inputContainer}>
                <input
                    type="number"
                    value={userAnswer}
                    onChange={(e) => setUserAnswer(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && checkAnswer()}
                    style={styles.input}
                    placeholder="Answer"
                />

                <button
                    onClick={checkAnswer}
                    style={styles.submitButton}
                >
                    Submit
                </button>
            </div>

            <div style={styles.feedback}>
                {feedback}
            </div>

            <div style={styles.score}>
                Score: <strong style={{ fontSize: '1.4rem' }}>{score}</strong>
            </div>
        </div>
    );
};

export default MathPowerGame;

// import React, { useState, useEffect, useCallback } from 'react';

// const MathPowerGame = () => {
//     const [mode, setMode] = useState('squares'); // 'squares', 'cubes', or a number (12-19) for tables
//     const [score, setScore] = useState(0);
//     const [currentNumber, setCurrentNumber] = useState(0);
//     const [currentMultiplier, setCurrentMultiplier] = useState(0);
//     const [userAnswer, setUserAnswer] = useState('');
//     const [feedback, setFeedback] = useState('');
//     const [isCorrect, setIsCorrect] = useState(null);
//     const [numberPool, setNumberPool] = useState([]); // eslint-disable-line no-unused-vars

//     const generateQuestion = useCallback(() => {
//         if (typeof mode === 'number') {
//             // For multiplication tables (12-19)
//             setNumberPool(prevPool => {
//                 let newPool = [...prevPool];
//                 if (newPool.length === 0) {
//                     newPool = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
//                     // Shuffle
//                     for (let i = newPool.length - 1; i > 0; i--) {
//                         const j = Math.floor(Math.random() * (i + 1));
//                         [newPool[i], newPool[j]] = [newPool[j], newPool[i]];
//                     }
//                 }
//                 setCurrentMultiplier(newPool[0]);
//                 return newPool.slice(1);
//             });
//         } else {
//             // For squares and cubes
//             setNumberPool(prevPool => {
//                 let newPool = [...prevPool];
//                 if (newPool.length === 0) {
//                     const max = mode === 'squares' ? 30 : 20;
//                     newPool = Array.from({ length: max }, (_, i) => i + 1);
//                     // Shuffle
//                     for (let i = newPool.length - 1; i > 0; i--) {
//                         const j = Math.floor(Math.random() * (i + 1));
//                         [newPool[i], newPool[j]] = [newPool[j], newPool[i]];
//                     }
//                 }
//                 setCurrentNumber(newPool[0]);
//                 return newPool.slice(1);
//             });
//         }
//         setUserAnswer('');
//         setFeedback('');
//         setIsCorrect(null);
//     }, [mode]);

//     useEffect(() => {
//         // Reset pools when mode changes
//         setNumberPool([]);
//         if (typeof mode === 'number') {
//             setCurrentMultiplier(0);
//         } else {
//             setCurrentNumber(0);
//         }
//     }, [mode]);

//     const checkAnswer = () => {
//         let correctAnswer;
//         if (mode === 'squares') {
//             correctAnswer = currentNumber * currentNumber;
//         } else if (mode === 'cubes') {
//             correctAnswer = currentNumber * currentNumber * currentNumber;
//         } else { // tables mode (12-19)
//             correctAnswer = mode * currentMultiplier;
//         }

//         if (parseInt(userAnswer) === correctAnswer) {
//             setScore(score + 1);
//             setFeedback('✅ Correct!');
//             setIsCorrect(true);
//         } else {
//             setFeedback(`❌ Wrong! Correct answer: ${correctAnswer}`);
//             setIsCorrect(false);
//         }
//         setTimeout(generateQuestion, 1500);
//     };

//     useEffect(() => {
//         generateQuestion();
//     }, [mode, generateQuestion]);

//     return (
//         <div style={{
//             textAlign: 'center',
//             maxWidth: '800px',
//             margin: '0 auto',
//             minHeight: 'calc(100vh - 340px)',
//             padding: '20px',
//             display: 'flex',
//             flexDirection: 'column',
//             justifyContent: 'center',
//             borderRadius: '16px',
//             boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
//             fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
//         }}>
//             <h1 style={{
//                 fontSize: 'clamp(1.5rem, 2vw, 1.5rem)',
//                 color: '#2c3e50',
//                 marginBottom: '30px',
//                 textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
//             }}>
//                 Math Power Challenge ⚡
//             </h1>

//             <div style={{
//                 display: 'flex',
//                 justifyContent: 'center',
//                 gap: '10px',
//                 marginBottom: '20px',
//                 flexWrap: 'wrap'
//             }}>
//                 <button
//                     onClick={() => setMode('squares')}
//                     style={{
//                         backgroundColor: mode === 'squares' ? '#4CAF50' : '#e0e0e0',
//                         color: mode === 'squares' ? 'white' : '#333',
//                         padding: '12px 24px',
//                         border: 'none',
//                         borderRadius: '50px',
//                         cursor: 'pointer',
//                         fontSize: 'clamp(0.9rem, 3vw, 1rem)',
//                         fontWeight: '600',
//                         transition: 'all 0.3s ease',
//                         boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
//                         minWidth: '120px'
//                     }}
//                 >
//                     Squares (1²-30²)
//                 </button>
//                 <button
//                     onClick={() => setMode('cubes')}
//                     style={{
//                         backgroundColor: mode === 'cubes' ? '#4CAF50' : '#e0e0e0',
//                         color: mode === 'cubes' ? 'white' : '#333',
//                         padding: '12px 24px',
//                         border: 'none',
//                         borderRadius: '50px',
//                         cursor: 'pointer',
//                         fontSize: 'clamp(0.9rem, 3vw, 1rem)',
//                         fontWeight: '600',
//                         transition: 'all 0.3s ease',
//                         boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
//                         minWidth: '120px'
//                     }}
//                 >
//                     Cubes (1³-20³)
//                 </button>
//             </div>

//             <div style={{
//                 marginBottom: '30px',
//                 textAlign: 'center'
//             }}>
//                 <h3 style={{ margin: '10px 0', color: '#2c3e50' }}>Multiplication Tables</h3>
//                 <div style={{
//                     display: 'flex',
//                     justifyContent: 'center',
//                     gap: '10px',
//                     flexWrap: 'wrap',
//                     maxWidth: '600px',
//                     margin: '0 auto'
//                 }}>
//                     {[12, 13, 14, 15, 16, 17, 18, 19].map((table) => (
//                         <button
//                             key={table}
//                             onClick={() => setMode(table)}
//                             style={{
//                                 backgroundColor: mode === table ? '#3498db' : '#e0e0e0',
//                                 color: mode === table ? 'white' : '#333',
//                                 padding: '10px 15px',
//                                 border: 'none',
//                                 borderRadius: '8px',
//                                 cursor: 'pointer',
//                                 fontSize: '1rem',
//                                 fontWeight: '600',
//                                 transition: 'all 0.3s ease',
//                                 boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
//                                 minWidth: '50px'
//                             }}
//                         >
//                             {table}
//                         </button>
//                     ))}
//                 </div>
//             </div>

//             <div key={mode === 'squares' || mode === 'cubes' ? currentNumber : currentMultiplier} style={{
//                 fontSize: 'clamp(1.5rem, 2vw, 1.5rem)',
//                 margin: '20px 0',
//                 color: isCorrect === true ? '#27ae60' : isCorrect === false ? '#e74c3c' : '#2c3e50',
//                 fontWeight: '700',
//                 transition: 'color 0.3s ease'
//             }}>
//                 {mode === 'squares' ? `What is ${currentNumber}²?` :
//                     mode === 'cubes' ? `What is ${currentNumber}³?` :
//                         `What is ${mode} × ${currentMultiplier}?`}
//             </div>

//             <div style={{
//                 display: 'flex',
//                 flexDirection: 'column',
//                 alignItems: 'center',
//                 gap: '15px',
//                 marginBottom: '20px'
//             }}>
//                 <input
//                     type="number"
//                     value={userAnswer}
//                     onChange={(e) => setUserAnswer(e.target.value)}
//                     onKeyPress={(e) => e.key === 'Enter' && checkAnswer()}
//                     style={{
//                         padding: '15px',
//                         fontSize: '1.2rem',
//                         width: '150px',
//                         textAlign: 'center',
//                         borderRadius: '8px',
//                         border: '2px solid #dfe6e9',
//                         outline: 'none',
//                         transition: 'all 0.3s ease',
//                         boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
//                         maxWidth: '80%'
//                     }}
//                     placeholder="Answer"
//                 />

//                 <button
//                     onClick={checkAnswer}
//                     style={{
//                         backgroundColor: '#007BFF',
//                         color: 'white',
//                         padding: '12px 30px',
//                         border: 'none',
//                         borderRadius: '50px',
//                         cursor: 'pointer',
//                         fontSize: '1.1rem',
//                         fontWeight: '600',
//                         transition: 'all 0.3s ease',
//                         boxShadow: '0 4px 8px rgba(0,123,255,0.3)',
//                         width: 'fit-content'
//                     }}
//                 >
//                     Submit
//                 </button>
//             </div>

//             <div style={{
//                 fontSize: '1.3rem',
//                 margin: '15px 0',
//                 minHeight: '30px',
//                 fontWeight: '500',
//                 color: isCorrect === true ? '#27ae60' : isCorrect === false ? '#e74c3c' : 'transparent',
//                 transition: 'all 0.3s ease'
//             }}>
//                 {feedback}
//             </div>

//             <div style={{
//                 fontSize: '1.2rem',
//                 color: '#2c3e50',
//                 fontWeight: '600',
//                 padding: '15px',
//                 backgroundColor: 'rgba(255,255,255,0.7)',
//                 borderRadius: '8px',
//                 boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
//             }}>
//                 Score: <strong style={{ fontSize: '1.4rem' }}>{score}</strong>
//             </div>
//         </div>
//     );
// };

// export default MathPowerGame;

// import React, { useState, useEffect, useCallback } from 'react';

// const MathPowerGame = () => {
//     const [mode, setMode] = useState('squares'); // 'squares' or 'cubes'
//     const [score, setScore] = useState(0);
//     const [currentNumber, setCurrentNumber] = useState(0);
//     const [userAnswer, setUserAnswer] = useState('');
//     const [feedback, setFeedback] = useState('');
//     const [isCorrect, setIsCorrect] = useState(null);

//     // Generate a random number (1-12 for squares, 1-10 for cubes)
//     // const generateQuestion = () => {
//     //     const max = mode === 'squares' ? 20 : 20;
//     //     const num = Math.floor(Math.random() * max) + 1;
//     //     setCurrentNumber(num);
//     //     setUserAnswer('');
//     //     setFeedback('');
//     //     setIsCorrect(null);
//     // };
//     const generateQuestion = useCallback(() => {
//         const max = mode === 'squares' ? 30 : 20;
//         const num = Math.floor(Math.random() * max) + 1;
//         setCurrentNumber(num);
//         setUserAnswer('');
//         setFeedback('');
//         setIsCorrect(null);
//     }, [mode]);  // Now stable between renders

//     // Check the user's answer
//     const checkAnswer = () => {
//         const correctAnswer = mode === 'squares'
//             ? currentNumber * currentNumber
//             : currentNumber * currentNumber * currentNumber;

//         if (parseInt(userAnswer) === correctAnswer) {
//             setScore(score + 1);
//             setFeedback('✅ Correct!');
//             setIsCorrect(true);
//         } else {
//             setFeedback(`❌ Wrong! Correct answer: ${correctAnswer}`);
//             setIsCorrect(false);
//         }
//         setTimeout(generateQuestion, 1500); // Next question after 1.5s
//     };

//     // Start a new game
//     useEffect(() => {
//         generateQuestion();
//     }, [mode, generateQuestion]);

//     return (
//         <div style={{
//             textAlign: 'center',
//             maxWidth: '70vh',
//             margin: '0 auto',
//             minHeight: 'calc(100vh - 340px)',
//             padding: '20px',
//             display: 'flex',
//             flexDirection: 'column',
//             justifyContent: 'center',
//             // background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
//             borderRadius: '16px',
//             boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
//             fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
//         }}>
//             <h1 style={{
//                 fontSize: 'clamp(1.5rem, 2vw, 1.5rem)',
//                 color: '#2c3e50',
//                 marginBottom: '30px',
//                 textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
//             }}>
//                 Math Power Challenge ⚡
//             </h1>

//             <div style={{
//                 display: 'flex',
//                 justifyContent: 'center',
//                 gap: '10px',
//                 marginBottom: '30px',
//                 flexWrap: 'wrap'
//             }}>
//                 <button
//                     onClick={() => setMode('squares')}
//                     style={{
//                         backgroundColor: mode === 'squares' ? '#4CAF50' : '#e0e0e0',
//                         color: mode === 'squares' ? 'white' : '#333',
//                         padding: '12px 24px',
//                         border: 'none',
//                         borderRadius: '50px',
//                         cursor: 'pointer',
//                         fontSize: 'clamp(0.9rem, 3vw, 1rem)',
//                         fontWeight: '600',
//                         transition: 'all 0.3s ease',
//                         boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
//                         flex: '1 1 auto',
//                         minWidth: '120px'
//                     }}
//                 >
//                     Squares (1²-30²)
//                 </button>
//                 <button
//                     onClick={() => setMode('cubes')}
//                     style={{
//                         backgroundColor: mode === 'cubes' ? '#4CAF50' : '#e0e0e0',
//                         color: mode === 'cubes' ? 'white' : '#333',
//                         padding: '12px 24px',
//                         border: 'none',
//                         borderRadius: '50px',
//                         cursor: 'pointer',
//                         fontSize: 'clamp(0.9rem, 3vw, 1rem)',
//                         fontWeight: '600',
//                         transition: 'all 0.3s ease',
//                         boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
//                         flex: '1 1 auto',
//                         minWidth: '120px'
//                     }}
//                 >
//                     Cubes (1³-20³)
//                 </button>
//             </div>

//             <div key={currentNumber} style={{
//                 fontSize: 'clamp(1.5rem, 2vw, 1.5rem)',
//                 margin: '20px 0',
//                 color: isCorrect === true ? '#27ae60' : isCorrect === false ? '#e74c3c' : '#2c3e50',
//                 fontWeight: '700',
//                 transition: 'color 0.3s ease'
//             }}>
//                 What is {currentNumber}{mode === 'squares' ? '²' : '³'}?
//             </div>

//             <div style={{
//                 display: 'flex',
//                 flexDirection: 'column',
//                 alignItems: 'center',
//                 gap: '15px',
//                 marginBottom: '20px'
//             }}>
//                 <input
//                     type="number"
//                     value={userAnswer}
//                     onChange={(e) => setUserAnswer(e.target.value)}
//                     onKeyPress={(e) => e.key === 'Enter' && checkAnswer()}
//                     style={{
//                         padding: '15px',
//                         fontSize: '1.2rem',
//                         width: '150px',
//                         textAlign: 'center',
//                         borderRadius: '8px',
//                         border: '2px solid #dfe6e9',
//                         outline: 'none',
//                         transition: 'all 0.3s ease',
//                         boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
//                         maxWidth: '80%'
//                     }}
//                     placeholder="Answer"
//                 />

//                 <button
//                     onClick={checkAnswer}
//                     style={{
//                         backgroundColor: '#007BFF',
//                         color: 'white',
//                         padding: '12px 30px',
//                         border: 'none',
//                         borderRadius: '50px',
//                         cursor: 'pointer',
//                         fontSize: '1.1rem',
//                         fontWeight: '600',
//                         transition: 'all 0.3s ease',
//                         boxShadow: '0 4px 8px rgba(0,123,255,0.3)',
//                         width: 'fit-content'
//                     }}
//                 >
//                     Submit
//                 </button>
//             </div>

//             <div style={{
//                 fontSize: '1.3rem',
//                 margin: '15px 0',
//                 minHeight: '30px',
//                 fontWeight: '500',
//                 color: isCorrect === true ? '#27ae60' : isCorrect === false ? '#e74c3c' : 'transparent',
//                 transition: 'all 0.3s ease'
//             }}>
//                 {feedback}
//             </div>

//             <div style={{
//                 // marginTop: 'auto',
//                 fontSize: '1.2rem',
//                 color: '#2c3e50',
//                 fontWeight: '600',
//                 padding: '15px',
//                 backgroundColor: 'rgba(255,255,255,0.7)',
//                 borderRadius: '8px',
//                 boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
//             }}>
//                 Score: <strong style={{ fontSize: '1.4rem' }}>{score}</strong>
//             </div>
//         </div>
//     );
// };

// export default MathPowerGame;